/**
 * @fileoverview フッター固定バナー制御
 * @description
 *   Phase 1（KV底部未到達）:
 *     KV スライダー（#js-kv-slider）の下端をページ絶対座標で保持し、
 *     スクロールのたびに現在のビューポート座標へ変換して banner.style.bottom に代入。
 *   Phase 2（KV底部到達: scrollY >= triggerY）:
 *     style.bottom をクリアし、CSS の .is-fixed { bottom: 2em } でウィンドウ右下に固定。
 *     フッター（.l-footer）がビューポートに入った場合は bottom を動的調整して重なりを防ぐ。
 *   切替座標の一致:
 *     KVがビューポートより高い場合、triggerY 到達時点で bottomPx = offsetPx となり
 *     Phase 1 末尾と Phase 2 冒頭の位置が一致してジャンプなし。
 *     KVがビューポートに収まる場合、KV完全スクロールアウト時点で Phase 2 へ。
 *   フェード:
 *     フェーズ切替時にフェードアウト → 位置切替 → フェードイン。
 *   スクロールハンドラーは scrollY との数値比較のみで、
 *   getBoundingClientRect はロード時・リサイズ時のみ呼ぶ（フッター回避は例外：
 *   banner が position:fixed のためスラッシングなく毎スクロール取得可能）。
 */

const initFixedBanner = () => {
	const banner = document.querySelector('.l-fixed-banner');
	const slider = document.querySelector('#js-kv-slider');
	const footer = document.querySelector('.l-footer');

	if (!banner || !slider) return;

	/** ページ絶対座標でのスライダー下端・スクロールトリガー・em換算値 */
	let sliderBottomAbs;
	let triggerY;
	let offsetPx;
	let currentPhase = null;

	/** フェード時間（CSS の transition と合わせる） */
	const FADE_MS = 400;
	/** フッター上端からバナー下辺までのギャップ（px） */
	const FOOTER_GAP_PX = 40;
	let fadeTimer = null;

	/**
	 * ロード・リサイズ時に各パラメーターを算出する。
	 */
	const calcParams = () => {
		const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
		offsetPx = 2 * rem;
		sliderBottomAbs = slider.getBoundingClientRect().bottom + window.scrollY;
		// KVがビューポートより高い場合: KV底部がビューポート底に達した瞬間（ジャンプなし）
		// KVがビューポートに収まる場合: KVが完全にスクロールアウトした時点
		const naturalTrigger = sliderBottomAbs - window.innerHeight;
		triggerY = naturalTrigger > 0 ? naturalTrigger : sliderBottomAbs;
	};

	/**
	 * Phase 2 中にフッターとバナーの重なりを防ぐよう bottom を調整する。
	 * フッターがビューポートに入っていない場合は CSS の bottom: Xem に委ねる。
	 * フッター上端がビューポート外（SPの縦長フッター内をスクロール中）はクランプして
	 * バナーがビューポート上端へ自然に退場するようにする。
	 */
	const avoidFooter = () => {
		if (!footer) return;
		// リアルタイムで取得（footerTopAbs の鮮度問題を回避）
		const footerTopViewport = footer.getBoundingClientRect().top;
		if (footerTopViewport >= window.innerHeight) {
			// フッターがまだビューポートに入っていない → CSS に委ねる
			banner.style.bottom = '';
			return;
		}
		// footerTopViewport が負（フッター上端がビューポート外へ出た）の場合は 0 でクランプ。
		// これにより SP の縦長フッターでも bottom が innerHeight を超えない。
		const footerVisibleHeight = window.innerHeight - Math.max(0, footerTopViewport);
		// バナー下辺がフッター上端の FOOTER_GAP_PX 上に来るよう bottom を設定
		banner.style.bottom = (footerVisibleHeight + FOOTER_GAP_PX) + 'px';
	};

	/**
	 * 指定フェーズの位置を即時適用する（フェードなし）。
	 * @param {number} phase
	 */
	const applyPhase = (phase) => {
		if (phase === 2) {
			banner.classList.add('is-fixed');
			banner.style.bottom = '';
			avoidFooter();
		} else {
			banner.classList.remove('is-fixed');
			const sliderBottomViewport = sliderBottomAbs - window.scrollY;
			const bottomPx = Math.max(offsetPx, window.innerHeight - sliderBottomViewport + offsetPx);
			banner.style.bottom = bottomPx + 'px';
		}
	};

	/**
	 * スクロールのたびに呼ばれる。
	 * フェーズ変化時のみフェードアウト→切替→フェードイン。
	 * Phase 2 中はフッター回避のため毎スクロール bottom を更新する。
	 */
	const onScroll = () => {
		const scrollY = window.scrollY;
		const newPhase = scrollY >= triggerY ? 2 : 1;

		if (newPhase !== currentPhase) {
			currentPhase = newPhase;
			clearTimeout(fadeTimer);
			banner.style.opacity = '0';
			fadeTimer = setTimeout(() => {
				applyPhase(newPhase);
				banner.style.opacity = '1';
			}, FADE_MS);
		} else if (newPhase === 1) {
			// Phase 1 中はスクロールに追従（フェードなし）
			applyPhase(1);
		} else {
			// Phase 2 中はフッター回避を毎スクロール更新（フェードなし）
			avoidFooter();
		}
	};

	const onResize = () => {
		calcParams();
		onScroll();
	};

	/**
	 * calcParams と各リスナーの登録を行う。
	 */
	const init = () => {
		calcParams();
		// 初期表示はフェードなしで即時適用
		const initialPhase = window.scrollY >= triggerY ? 2 : 1;
		currentPhase = initialPhase;
		applyPhase(initialPhase);
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onResize, { passive: true });
	};

	const firstImg = slider.querySelector('.splide__slide img');

	if (firstImg && firstImg.complete && firstImg.naturalHeight > 0) {
		init();
	} else if (firstImg) {
		firstImg.addEventListener('load', () => init(), { once: true });
	} else {
		window.addEventListener('load', () => init(), { once: true });
	}
};

document.addEventListener('DOMContentLoaded', initFixedBanner);
